import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import ReactSelect from 'react-select';

import usePrefix from 'utils/usePrefix';
import { ResponseError } from 'utils/api';
import { interventionPlanIntervenerApi } from 'utils/api/ratings';
import { RatingResponse } from 'utils/api/notice';
import { useApp } from 'App';
import { ActionTypes } from 'App/types';
import { notificationTypes } from 'utils/constants';
import Modal from 'components/Modal';
import {
  RatingFormActionsStyled,
  RatingFormStyled,
  RatingSelectWrapperStyled,
} from 'components/Rating/styles';
import { Label } from 'components/FormField/styles';
import Button from 'components/Button';
import TextAreaComponent from 'components/TextAreaComponent';

import { RatingModalContentStyled } from './styles';

interface Props {
  noticeId: number;
  hasRating?: boolean;
  initialRating?: number;
  initialNote?: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (response: RatingResponse) => void;
}

// eslint-disable-next-line no-magic-numbers
const RATING_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }));

const RatingModal: FC<Props> = ({ noticeId, isOpen, onClose, onSaved, initialRating, initialNote = '', hasRating }) => {
  const t = usePrefix('Rating');
  const [, dispatch] = useApp();

  const [selectedRating, setSelectedRating] = useState<{ value: number; label: string } | null>(
    initialRating ? { value: initialRating, label: String(initialRating) } : null
  );
  const [noteText, setNoteText] = useState(initialNote);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedRating(initialRating ? { value: initialRating, label: String(initialRating) } : null);
      setNoteText(initialNote);
    }
  }, [isOpen, initialRating, initialNote]);


  const onSave = useCallback(async () => {
    if (!selectedRating) return;
    setIsSubmitting(true);
    try {
      const response = await interventionPlanIntervenerApi.submitRating(noticeId, {
        stars: selectedRating.value,
        comment: noteText.trim() ? noteText : null,
      });
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { type: notificationTypes.success },
      });
      onSaved(response);
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [noticeId, selectedRating, noteText, dispatch, onSaved]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} centered>
      <RatingModalContentStyled>
        <h3>{hasRating ? t('cta_edit_rating') : t('cta_add_rating')}</h3>
        <RatingFormStyled>
          <RatingSelectWrapperStyled>
            <Label htmlFor="select_rating">{t('select_rating')}</Label>
            <ReactSelect
              options={RATING_OPTIONS}
              value={selectedRating}
              onChange={(val) => setSelectedRating(val as { value: number; label: string })}
              isSearchable={false}
              placeholder={t('select_rating')}
              id="select_rating"
            />
          </RatingSelectWrapperStyled>
          <TextAreaComponent
            id="note"
            label={t('label_note')}
            placeholder={t('notePlaceholder')}
            text={noteText}
            setText={setNoteText}
          />
          <RatingFormActionsStyled>
            <Button label={t('cta_cancel')} color="secondary" onClick={onClose} />
            <Button
              label={t('cta_save')}
              color="primary"
              onClick={onSave}
              disabled={!selectedRating || isSubmitting}
            />
          </RatingFormActionsStyled>
        </RatingFormStyled>
      </RatingModalContentStyled>
    </Modal>
  );
};

export default memo(RatingModal);
